import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ip_back } from "../config";
import { applyFullBrandTheme, syncBrandAfterAuth } from "../utils/brandAuth";
import { useBrand } from "../context/BrandContext";
import { usePermissions } from "../context/Permissioncontext";
import "./SetPassword.css";

const apiBase = () => ip_back.replace(/\/$/, "");

function SetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = String(searchParams.get("token") || "").trim();
  const { refreshBrand } = useBrand();
  const { loadPermissions } = usePermissions();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);

  const brand = preview?.brand || null;
  const primary = brand?.color_primary || "#0877BC";
  const primaryDark = brand?.color_primary_dark || "#065a8f";
  const logoUrl = brand?.logo_url || brand?.logo_light_url || null;
  const brandName = brand?.brand_name || preview?.club_name || "SmartTT Club";

  const displayName = useMemo(() => {
    const parts = [preview?.first_name, preview?.last_name].filter(Boolean);
    return parts.join(" ") || preview?.email || "";
  }, [preview]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!token) {
        setError(t("setPasswordMissingToken") || "This confirmation link is incomplete.");
        setLoading(false);
        return;
      }
      try {
        const res = await fetch(
          `${apiBase()}/api/parent/child-password-setup?token=${encodeURIComponent(token)}`
        );
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !json.success) {
          throw new Error(json.message || "Invalid or expired confirmation link.");
        }
        if (cancelled) return;
        setPreview(json.data || null);
        if (json.data?.brand) {
          try {
            applyFullBrandTheme(json.data.brand, { persist: true });
          } catch {
            /* ignore */
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Invalid or expired confirmation link.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [token, t]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError(t("setPasswordTooShort") || "Use at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError(t("setPasswordMismatch") || "Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${apiBase()}/api/parent/child-password-setup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          password,
          passwordConfirmation: confirm,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        throw new Error(json.message || "Could not set password.");
      }
      const data = json.data || {};
      if (data.user_token) {
        localStorage.setItem("token", data.user_token);
        const userPayload = {
          ...data,
          user_email: data.email,
          emailVerification: true,
        };
        localStorage.setItem("user", JSON.stringify(userPayload));
        try {
          await syncBrandAfterAuth(userPayload, refreshBrand);
        } catch {
          /* ignore */
        }
        try {
          await loadPermissions?.(data.user_id);
        } catch {
          /* ignore */
        }
      }
      setDone(true);
      window.setTimeout(() => navigate("/home", { replace: true }), 900);
    } catch (err) {
      setError(err.message || "Could not set password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="set-password"
      style={{
        "--sp-primary": primary,
        "--sp-primary-dark": primaryDark,
      }}
    >
      <div className="set-password__card">
        {logoUrl ? (
          <img className="set-password__logo" src={logoUrl} alt={brandName} />
        ) : (
          <p className="set-password__brand">{brandName}</p>
        )}

        {loading ? (
          <div className="set-password__state">
            <div className="set-password__spinner" aria-hidden />
            <p>{t("loading") || "Loading…"}</p>
          </div>
        ) : error && !preview ? (
          <div className="set-password__state">
            <h1>{t("setPasswordInvalidTitle") || "Link unavailable"}</h1>
            <p>{error}</p>
            <button
              type="button"
              className="set-password__btn"
              onClick={() => navigate("/login")}
            >
              {t("login") || "Log in"}
            </button>
          </div>
        ) : done ? (
          <div className="set-password__state">
            <h1>{t("setPasswordSuccessTitle") || "You're all set"}</h1>
            <p>
              {t("setPasswordSuccessBody") ||
                "Email confirmed and password saved. Opening your club…"}
            </p>
          </div>
        ) : (
          <>
            <h1>{t("setPasswordTitle") || "Set your password"}</h1>
            <p className="set-password__sub">
              {t("setPasswordSubtitle", {
                name: displayName,
                club: preview?.club_name || brandName,
                defaultValue:
                  "Confirm your account for {{club}} and choose a password to log in.",
              })}
            </p>
            {preview?.email ? (
              <p className="set-password__email">{preview.email}</p>
            ) : null}
            <form className="set-password__form" onSubmit={onSubmit}>
              <label className="set-password__label">
                {t("password") || "Password"}
                <input
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={8}
                  required
                />
              </label>
              <label className="set-password__label">
                {t("confirmPassword") || "Confirm password"}
                <input
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  minLength={8}
                  required
                />
              </label>
              {error ? <p className="set-password__error">{error}</p> : null}
              <button
                type="submit"
                className="set-password__btn"
                disabled={submitting}
              >
                {submitting
                  ? t("saving") || "Saving…"
                  : t("setPasswordCta") || "Confirm and continue"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default SetPassword;
